import { GridBaseDataProvider } from '../../grid/GridBaseDataProvider';
import {
  GetRowsRequest,
  GetRowsResponse,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
} from '../../grid/types/common';
import { Column, ColumnKeys, RawConfig, RawEventConfigGridItem, Statuses } from '../types';
import { desanitizeObject, sanitizeObject } from '../../utils/sanitization.utils';
import { EventMappings } from '../eventMappings';
import { filterRows } from '../../grid/utils/filters';
import { FilterInstance } from '../../grid/utils/filters/types';
import { createEventTypeTranslations, createRequirementTranslations } from '../eventMappings/Translations';

export class RawEventsConfigurationGridDataProvider extends GridBaseDataProvider<any, any> {
  private readonly allEventMappingsBySensorName: { [sensorName: string]: EventMappings };
  private filters: Array<FilterInstance<ColumnKeys>> = [];
  private readonly eventTypeTranslations;
  private readonly requirementsTranslations;

  constructor(dependencies: {
    readConfig: (params: { companyId: string }) => Promise<RawConfig>;
    translateFn: Function;
    getCompanyId: () => string;
    setConfigId: (configId: string, companyId: string) => void;
  }) {
    super();

    this.injectDependencies(dependencies);
    this.allEventMappingsBySensorName = this.dependencies.allEventMappingsBySensorName;

    this.eventTypeTranslations = createEventTypeTranslations(this.dependencies.translateFn);
    this.requirementsTranslations = createRequirementTranslations(this.dependencies.translateFn);
  }

  public async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    // We don't use summary
    return {
      summary: '',
      numItems: 0,
    };
  }

  public async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetRowsResponse> {
    const rows: Array<RawEventConfigGridItem> = [];

    // Use currently selected company or the current company if no filter was set
    const companyIdFilter = request.filters.find(filter => 'companyId' in filter) ?? {
      companyId: {
        exactMatch: this.dependencies.getCompanyId()
      }
    };
    const selectedCompanyId = companyIdFilter.companyId.exactMatch;
    const rawConfig = (await this.dependencies.api.readConfig({
      companyId: selectedCompanyId,
    })) as RawConfig;

    this.dependencies.setFeatureFlags(!!rawConfig['isMdrOn'], !!rawConfig['hasEdrStorageAddon']);
    this.dependencies.setConfigId(rawConfig['_id'], selectedCompanyId);

    const ignoredFields: string[] = ['_id', 'isMdrOn', 'hasEdrStorageAddon'];

    Object.entries(rawConfig).forEach(([sensorName, sensorConfig]) => {
      if (ignoredFields.includes(sensorName)) {
        return;
      }

      if (!(sensorName in this.allEventMappingsBySensorName)) {
        console.error(`Unknown raw events config sensor ${sanitizeObject(sensorName)}`);
        return;
      }
      const sensorMapping = this.allEventMappingsBySensorName[sensorName];

      Object.entries(sensorConfig).forEach(([eventType, eventStatus]) => {
        if (!(eventType in sensorMapping.mappings)) {
          console.error(`Unknown raw events config event type ${sanitizeObject(eventType)}`);
          return;
        }

        const eventTypeDetails = sensorMapping.mappings[eventType];
        const row = {
          ...eventTypeDetails,
          id: `${sensorName}:${eventType}`,
          [ColumnKeys.STATUS]: eventStatus as Statuses,
          [ColumnKeys.SENSOR_TYPE]: sensorMapping.sensorName,
          [ColumnKeys.EVENT_TYPE]: this.eventTypeTranslations[eventTypeDetails[ColumnKeys.EVENT_TYPE]],
          [ColumnKeys.REQUIREMENTS]: eventTypeDetails[ColumnKeys.REQUIREMENTS].map(
            requirement => this.requirementsTranslations[requirement]
          ),
          eventTypeRawName: eventType,
        };

        rows.push(row);
      });
    });

    rows.sort(function (a, b) {
      if (a[ColumnKeys.CATEGORY] < b[ColumnKeys.CATEGORY]) return -1;
      if (a[ColumnKeys.CATEGORY] > b[ColumnKeys.CATEGORY]) return 1;
      return 0;
    });

    // Ignore the companyId as we already sent it to the backend and the records were filtered
    const filters = this.filters.filter(filter => !('companyId' in filter));
    const filteredRows = filterRows(filters, rows);
    return desanitizeObject({ rawEvents: filteredRows });
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    this.filters = request.filters;
    return;
  }

  public shouldDataReloadOnResetView(): boolean {
    return true;
  }
}
